/**
 * Normalizes API validation errors from new format (422 with `errors`) to old format (400 with `invalid_fields`)
 * This ensures backward compatibility with existing components that expect the old format.
 *
 * Old format: { status: 400, invalid_fields: { email: "Required." } }
 * New format: { status: 422, errors: { email: ["Required."] } }
 */

interface OldFormatError {
  invalid_fields?: Record<string, string>
  [key: string]: unknown
}

interface NewFormatError {
  errors?: Record<string, string | string[]>
  message?: string
  [key: string]: unknown
}

type ApiError = OldFormatError | NewFormatError

/**
 * Checks if the HTTP status code indicates a validation error
 */
export const isValidationError = (status: number): boolean =>
  status === 400 || status === 422

/**
 * Normalizes API error response to ensure backward compatibility.
 * Converts new format (errors array) to old format (invalid_fields string).
 */
export const normalizeApiError = <T extends ApiError>(error: T): T => {
  // If already has invalid_fields (old format), return as-is
  if ('invalid_fields' in error && error.invalid_fields) {
    return error
  }

  // Convert new format: errors → invalid_fields (take first message from array).
  // Only field-keyed objects are real validation errors. A positional array
  // (e.g. errors: [["..."]]) is a global/business error, not field validation —
  // normalizing it would invent phantom fields ({ "0": ... }) that no form
  // renders, swallowing the message. Leave those untouched so message/errors
  // survive for getErrorMessage to surface.
  if (
    'errors' in error &&
    error.errors &&
    typeof error.errors === 'object' &&
    !Array.isArray(error.errors)
  ) {
    const invalidFields: Record<string, string> = {}

    for (const [field, messages] of Object.entries(error.errors)) {
      if (Array.isArray(messages)) {
        // Take first message from array, or empty string if array is empty
        invalidFields[field] = messages[0] ?? ''
      } else if (typeof messages === 'string') {
        // Fallback: handle string value directly
        invalidFields[field] = messages
      } else {
        invalidFields[field] = ''
      }
    }

    return { ...error, invalid_fields: invalidFields }
  }

  return error
}
